import { useMemo } from 'react';
import { MapPin } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { LPages } from '@/components/i18n';
import { useLocalizations } from '@/hooks/useLocalizations';
import { useLocalizedLocations } from '@/hooks/useLocalizedLocations';
import { RESPONSIVE } from '@/design-system';
import type { LocationWithCoordinates } from '@/types/taxExplorer';

interface ExploreLocationPromptProps {
  open: boolean;
  locations: LocationWithCoordinates[];
  onSelect: (locationId: number) => void;
  onDismiss: () => void;
}

function flag(code: string | null): string {
  if (!code || code.length !== 2) return '🌐';
  return String.fromCodePoint(...code.toUpperCase().split('').map((c) => 127397 + c.charCodeAt(0)));
}

export function ExploreLocationPrompt({ open, locations, onSelect, onDismiss }: ExploreLocationPromptProps): JSX.Element {
  const { lPages } = useLocalizations();
  const { locations: localized } = useLocalizedLocations();

  const items = useMemo(() => {
    const nameByIso = new Map(localized.map((l) => [(l.iso ?? '').toUpperCase(), l.name]));
    return locations
      .map((l) => ({ id: l.location_id, iso: l.iso_alpha2, name: nameByIso.get((l.iso_alpha2 ?? '').toUpperCase()) || l.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [locations, localized]);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onDismiss(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className={RESPONSIVE.flexRowCenterGap2}>
            <MapPin className="h-5 w-5 text-primary" aria-hidden="true" />
            <LPages id="explore.location_prompt.title" defaultValue="Choose a location to start" />
          </DialogTitle>
          <DialogDescription>
            <LPages
              id="explore.location_prompt.description"
              defaultValue="We couldn't detect your location. Pick where you live or pay tax to see rates, rankings and comparisons for it."
            />
          </DialogDescription>
        </DialogHeader>
        <Command className="rounded-md border border-border">
          <CommandInput placeholder={lPages('explore.location_prompt.search_placeholder', { defaultValue: 'Search locations' })} />
          <CommandList className="max-h-72">
            <CommandEmpty>
              <LPages id="explore.location_prompt.no_results" defaultValue="No location found" />
            </CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem key={item.id} value={`${item.name} ${item.iso ?? ''}`} onSelect={() => onSelect(item.id)}>
                  <span className="me-2" aria-hidden="true">{flag(item.iso)}</span>
                  <span>{item.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        <Button type="button" variant="ghost" onClick={onDismiss}>
          <LPages id="explore.location_prompt.browse_all" defaultValue="Browse all locations" />
        </Button>
      </DialogContent>
    </Dialog>
  );
}
